import { createContext } from 'react';

export const ThemeContext = createContext();

export const ThemeContextData = {
    light: {
        // Legacy class-based tokens (kept for backward compatibility)
        textNormal: 'text-gray-600',
        textNormalHover: 'text-gray-800',
        textFocus: 'text-gray-900',
        background: 'bg-white',
        backgroundSecondary: 'bg-brand-200',
        backgroundTertiary: 'bg-brand-50',
        backgroundGradientVia: 'via-gray-200',
        backgroundGradientTo: 'to-gray-200',
        backgroundGradientFrom: 'from-transparent',
        themeColour: 'brand',
        buttonPrimary: 'bg-brand-500 hover:bg-brand-600 text-white',

        // Semantic design tokens
        colors: {
            bg: {
                primary: '#ffffff',
                secondary: '#D8E4E6',
                tertiary: '#F7F8F8',
                card: '#ffffff',
                overlay: 'rgba(0, 0, 0, 0.5)',
                input: '#F7F8F8',
            },
            text: {
                primary: '#111827',
                secondary: '#4B5563',
                muted: '#9CA3AF',
                inverse: '#ffffff',
                brand: '#5396BD',
            },
            border: {
                default: '#E5E7EB',
                focus: '#5396BD',
                card: '#EBEFEF',
            },
        },

        // CSS variable map
        cssVars: {
            '--color-bg-primary': '#ffffff',
            '--color-bg-secondary': '#D8E4E6',
            '--color-bg-tertiary': '#F7F8F8',
            '--color-bg-card': '#ffffff',
            '--color-bg-overlay': 'rgba(0, 0, 0, 0.5)',
            '--color-bg-input': '#F7F8F8',
            '--color-text-primary': '#111827',
            '--color-text-secondary': '#4B5563',
            '--color-text-muted': '#9CA3AF',
            '--color-text-inverse': '#ffffff',
            '--color-text-brand': '#5396BD',
            '--color-border-default': '#E5E7EB',
            '--color-border-focus': '#5396BD',
            '--color-border-card': '#EBEFEF',
            '--color-nav-bg': 'rgba(255, 255, 255, 0.8)',
            '--color-footer-bg': '#D8E4E6',
            '--color-skeleton': '#E5E7EB',
            '--color-skeleton-shine': '#F3F4F6',
            '--shadow-card': '0 2px 8px -2px rgba(0, 0, 0, 0.08), 0 4px 16px -4px rgba(0, 0, 0, 0.06)',
            '--shadow-card-hover': '0 8px 24px -4px rgba(0, 0, 0, 0.12), 0 12px 32px -8px rgba(0, 0, 0, 0.10)',
        },
    },
    dark: {
        // Legacy class-based tokens
        textNormal: 'text-gray-400',
        textNormalHover: 'text-white',
        textFocus: 'text-white',
        background: 'bg-brand-800',
        backgroundSecondary: 'bg-brand-900',
        backgroundTertiary: 'bg-brand-700',
        backgroundGradientVia: 'via-brand-900',
        backgroundGradientTo: 'to-brand-900',
        backgroundGradientFrom: 'from-transparent',
        themeColour: 'brand',
        buttonPrimary: 'bg-brand-600 hover:bg-brand-700 text-white',

        // Semantic design tokens
        colors: {
            bg: {
                primary: '#18273d',
                secondary: '#0E132B',
                tertiary: '#3F6BAA',
                card: '#1e3350',
                overlay: 'rgba(0, 0, 0, 0.7)',
                input: '#0E132B',
            },
            text: {
                primary: '#F9FAFB',
                secondary: '#9CA3AF',
                muted: '#6B7280',
                inverse: '#111827',
                brand: '#64B2FF',
            },
            border: {
                default: '#374151',
                focus: '#64B2FF',
                card: '#1e3350',
            },
        },

        // CSS variable map
        cssVars: {
            '--color-bg-primary': '#18273d',
            '--color-bg-secondary': '#0E132B',
            '--color-bg-tertiary': '#3F6BAA',
            '--color-bg-card': '#1e3350',
            '--color-bg-overlay': 'rgba(0, 0, 0, 0.7)',
            '--color-bg-input': '#0E132B',
            '--color-text-primary': '#F9FAFB',
            '--color-text-secondary': '#9CA3AF',
            '--color-text-muted': '#6B7280',
            '--color-text-inverse': '#111827',
            '--color-text-brand': '#64B2FF',
            '--color-border-default': '#374151',
            '--color-border-focus': '#64B2FF',
            '--color-border-card': '#1e3350',
            '--color-nav-bg': 'rgba(24, 39, 61, 0.85)',
            '--color-footer-bg': '#0E132B',
            '--color-skeleton': '#374151',
            '--color-skeleton-shine': '#4B5563',
            '--shadow-card': '0 2px 8px -2px rgba(0, 0, 0, 0.3), 0 4px 16px -4px rgba(0, 0, 0, 0.25)',
            '--shadow-card-hover': '0 8px 24px -4px rgba(0, 0, 0, 0.4), 0 12px 32px -8px rgba(0, 0, 0, 0.35)',
        },
    },
};

export const applyThemeCssVars = (themeName) => {
    const themeData = ThemeContextData[themeName];
    if (!themeData?.cssVars) return;
    const root = document.documentElement;
    Object.entries(themeData.cssVars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
    root.setAttribute('data-theme', themeName);
};
